import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { TemplateCategoryApiService } from '../services/template-category-api.service';
import {
  TemplateCategory,
  TemplateCategoryRequest,
} from '../models/template.model';

@Component({
  selector: 'app-template-category-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './template-category-form.component.html',
})
export class TemplateCategoryFormComponent implements OnInit {
  @Input() drawerMode = true;
  @Output() saved = new EventEmitter<TemplateCategory>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  categoryId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private api: TemplateCategoryApiService
  ) {
    this.initForm();
  }

  ngOnInit(): void {}

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(250)]],
    });
  }

  reset(): void {
    this.isEditing = false;
    this.categoryId = null;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.initForm();
  }

  loadCategory(id: string): void {
    this.loading = true;
    this.isEditing = true;
    this.categoryId = id;
    this.api.getById(id).subscribe({
      next: (item) => {
        this.form.patchValue({
          name: item.name,
          description: item.description ?? '',
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar categoría';
        this.loading = false;
      },
    });
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';
    const v = this.form.value;
    const body: TemplateCategoryRequest = {
      name: (v.name ?? '').trim(),
      description: v.description?.trim() || null,
    };
    const req$ = this.isEditing && this.categoryId
      ? this.api.update(this.categoryId, body)
      : this.api.create(body);

    req$.subscribe({
      next: (item) => {
        this.saving = false;
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        if (err.status === 409) {
          this.error = 'Ya existe una categoría con ese nombre.';
        } else {
          this.error = err.error?.message || 'Error al guardar';
        }
      },
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength'])
      return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Campo inválido';
  }
}
